/* eslint-disable max-len */
import { AfterViewInit, Component, Injector, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  ActivationStart,
  Event,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router
} from '@angular/router';
import { LoadingIndicatorService } from 'app/core/loading-indicator.service';
import { LoggingService } from 'app/core/logging.service';
import { Subscription } from 'rxjs';
import { environment } from './../environments/environment';
import { GlobalApplicationSettings } from './app.settings';
import { AuthService } from './auth/auth.service';
import { HeaderComponent } from './core/header/header.component';
import { TrackingService } from './shared/tracking.service';

declare let window: any;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  designSystemsLoaded = false;

  @ViewChild(HeaderComponent) headerComponent: HeaderComponent;

  magentaSSOKeepAliveEndpoint: string | null = null;
  navigationHidden: boolean;
  routerSubscription: Subscription;

  constructor(
    private readonly router: Router,
    private readonly loggingService: LoggingService,
    private readonly injector: Injector,
    private readonly trackingService: TrackingService,
    private readonly authService: AuthService,
    readonly loadingIndicatorService: LoadingIndicatorService
  ) {
    if (environment.production) {
      this.appendGaTrackingCode();
      this.appendVwoSmartCode();
    } else {
      this.loggingService.info(this, 'Not adding analytics because its not a production build');
    }
  }

  navigationInterceptor(event: Event): void {
    if (event instanceof NavigationStart) {
      this.loadingIndicatorService.setPageLoading();
      this.setNavigationHidden(event.url);
    }

    if (event instanceof NavigationEnd) {
      this.loadingIndicatorService.setPageInitialized();

      // each component should say when it's ready, it's usually not on NavigationEnd
      this.trackingService.pageView();
    }

    if (event instanceof NavigationCancel) {
      this.loadingIndicatorService.setPageInitialized();
      this.loadingIndicatorService.setPageLoadComplete();
    }

    if (event instanceof NavigationError) {
      this.loadingIndicatorService.setPageInitialized();
      this.loadingIndicatorService.setPageLoadFailed();
    }

    if (event instanceof ActivationStart) {
      this.setHandleHttpExceptions(event);
    }
  }

  ngAfterViewInit(): void {
    this.waitForDesignSystems();
  }

  ngOnDestroy(): void {
    this.routerSubscription.unsubscribe();
  }

  ngOnInit() {
    this.routerSubscription = this.router.events.subscribe((event: Event) => {
      this.navigationInterceptor(event);
    });

    this.authService.keepAliveTriggered.subscribe({
      next: (val) => {
        this.magentaSSOKeepAliveEndpoint = null;

        setTimeout(() => {
          this.magentaSSOKeepAliveEndpoint = val;
        }, 50);
      }
    });
  }

  private appendGaTrackingCode() {
    try {
      const script = document.createElement('script');
      script.innerHTML = `
        (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer', '${this.trackingService.measurementId}');
        `;
      document.head.appendChild(script);
    } catch (ex) {
      this.loggingService.error(this, 'Error appending google analytics', null, undefined, ex);
    }
  }

  private appendVwoSmartCode() {
    try {
      const script = document.createElement('script');
      script.innerHTML = `
        window._vwo_code || (function() {
        var account_id=741212,
        version=2.1,
        settings_tolerance=2000,
        hide_element='body',
        hide_element_style = 'opacity:0 !important;filter:alpha(opacity=0) !important;background:none !important;transition:none !important;',
        /* DO NOT EDIT BELOW THIS LINE */
        f=false,w=window,d=document,v=d.querySelector('#vwoCode'),cK='_vwo_'+account_id+'_settings',cc={};try{var c=JSON.parse(localStorage.getItem('_vwo_'+account_id+'_config'));cc=c&&typeof c==='object'?c:{}}catch(e){}var stT=cc.stT==='session'?w.sessionStorage:w.localStorage;code={nonce:v&&v.nonce,use_existing_jquery:function(){return typeof use_existing_jquery!=='undefined'?use_existing_jquery:undefined},library_tolerance:function(){return typeof library_tolerance!=='undefined'?library_tolerance:undefined},settings_tolerance:function(){return cc.sT||settings_tolerance},hide_element_style:function(){return'{'+(cc.hES||hide_element_style)+'}'},hide_element:function(){if(performance.getEntriesByName('first-contentful-paint')[0]){return''}return typeof cc.hE==='string'?cc.hE:hide_element},getVersion:function(){return version},finish:function(e){if(!f){f=true;var t=d.getElementById('_vis_opt_path_hides');if(t)t.parentNode.removeChild(t);if(e)(new Image).src='https://dev.visualwebsiteoptimizer.com/ee.gif?a='+account_id+e}},finished:function(){return f},addScript:function(e){var t=d.createElement('script');t.type='text/javascript';if(e.src){t.src=e.src}else{t.text=e.text}v&&t.setAttribute('nonce',v.nonce);d.getElementsByTagName('head')[0].appendChild(t)},load:function(e,t){var n=this.getSettings(),i=d.createElement('script'),r=this;t=t||{};if(n){i.textContent=n;d.getElementsByTagName('head')[0].appendChild(i);if(!w.VWO||VWO.caE){stT.removeItem(cK);r.load(e)}}else{var o=new XMLHttpRequest;o.open('GET',e,true);o.withCredentials=!t.dSC;o.responseType=t.responseType||'text';o.onload=function(){if(t.onloadCb){return t.onloadCb(o,e)}if(o.status===200||o.status===304){_vwo_code.addScript({text:o.responseText})}else{_vwo_code.finish('&e=loading_failure:'+e)}};o.onerror=function(){if(t.onerrorCb){return t.onerrorCb(e)}_vwo_code.finish('&e=loading_failure:'+e)};o.send()}},getSettings:function(){try{var e=stT.getItem(cK);if(!e){return}e=JSON.parse(e);if(Date.now()>e.e){stT.removeItem(cK);return}return e.s}catch(e){return}},init:function(){if(d.URL.indexOf('__vwo_disable__')>-1)return;var e=this.settings_tolerance();w._vwo_settings_timer=setTimeout(function(){_vwo_code.finish();stT.removeItem(cK)},e);var t;if(this.hide_element()!=='body'){t=d.createElement('style');var n=this.hide_element(),i=n?n+this.hide_element_style():'',r=d.getElementsByTagName('head')[0];t.setAttribute('id','_vis_opt_path_hides');v&&t.setAttribute('nonce',v.nonce);t.setAttribute('type','text/css');if(t.styleSheet)t.styleSheet.cssText=i;else t.appendChild(d.createTextNode(i));r.appendChild(t)}else{t=d.getElementsByTagName('head')[0];var i=d.createElement('div');i.style.cssText='z-index: 2147483647 !important;position: fixed !important;left: 0 !important;top: 0 !important;width: 100% !important;height: 100% !important;background: white !important;display: block !important;';i.setAttribute('id','_vis_opt_path_hides');i.classList.add('_vis_hide_layer');t.parentNode.insertBefore(i,t.nextSibling)}var o=window._vis_opt_url||d.URL,s='https://dev.visualwebsiteoptimizer.com/j.php?a='+account_id+'&u='+encodeURIComponent(o)+'&vn='+version;if(w.location.search.indexOf('_vwo_xhr')!==-1){this.addScript({src:s})}else{this.load(s+'&x=true')}}};w._vwo_code=code;code.init();})();
      `;
      document.head.appendChild(script);
    } catch (ex) {
      this.loggingService.error(this, 'Error appending VWO Smart Code', null, undefined, ex);
    }
  }

  private setHandleHttpExceptions(activation: ActivationStart) {
    const settings = this.injector.get<GlobalApplicationSettings>(GlobalApplicationSettings);

    let handleHttpExceptions = true;
    if (activation && activation.snapshot && activation.snapshot.data) {
      handleHttpExceptions = activation.snapshot.data['handleHttpExceptions'] !== false;
    }

    settings.handleHttpExceptions = handleHttpExceptions;
  }

  private setNavigationHidden(url: string): void {
    const isNavHiddenStored = localStorage.getItem('nav_off') === '1';
    const navHidden = url.indexOf('nav=off') > -1 || isNavHiddenStored;
    if ((window as any).navigationHidden === undefined || navHidden) {
      (window as any).navigationHidden = navHidden;
      if (isNavHiddenStored) {
        localStorage.removeItem('nav_off');
      }
    }

    this.navigationHidden = (window as any).navigationHidden;
  }

  private waitForDesignSystems() {
    if (typeof window.TMobileDesignSystem !== 'undefined') {
      // variable exists, do what you want
      this.headerComponent.initialize();
      this.designSystemsLoaded = true;
    } else {
      setTimeout(() => this.waitForDesignSystems(), 250);
    }
  }
}
